import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import Request from '@framework/api/Rpc/Request';
import withCache from '@framework/with-cache';
import { withServerCache, type IServerCacheOptions } from '@/creditflowresearch/infrastructure/cache/withServerCache';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import BaseRepository from '@/creditflowresearch/infrastructure/repository/BaseRepository';
import type {
  IAbsBondScreenerDealCollectionResponse,
  IAbsBondScreenerGetDealsRequest,
  IAbsBondScreenerGetTranchesRequest,
  IAbsBondScreenerTrancheCollectionResponse,
  IAbsSearchLeadsPayload,
  IBondScreenerCompanyListResponse,
  ICompanyListPayload,
  ICorporateBondScreenerGetTranchesRequest,
  ICorporateBondScreenerTrancheCollectionResponse,
  IGetSelectedItemsRequest,
  ILeadCompany,
  ISearchLeadsPayload,
  TBondScreenerFilterBoundaries,
} from '@/creditflowresearch/domain/vo/Bondscreener';

@injectable()
class BondScreenersRepository extends BaseRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) rpc: IDispatcher,
  ) {
    super(rpc);
  }

  @withServerCache({
    scope: 'BondScreeners',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getTranches(
    payload: ICorporateBondScreenerGetTranchesRequest,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need for decorator
    _cacheOptions?: IServerCacheOptions,
  ): Promise<ICorporateBondScreenerTrancheCollectionResponse> {
    const request = new Request('cfr.bondscreener.corp.get_tranches', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async getAbsTranches(
    payload: IAbsBondScreenerGetTranchesRequest,
  ): Promise<IAbsBondScreenerTrancheCollectionResponse> {
    const request = new Request('cfr.bondscreener.abs.get_tranches', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async getAbsDeals(
    payload: IAbsBondScreenerGetDealsRequest,
  ): Promise<IAbsBondScreenerDealCollectionResponse> {
    const request = new Request('cfr.bondscreener.abs.get_deals', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async getSelectedCorporateTranches(
    payload: IGetSelectedItemsRequest,
  ): Promise<ICorporateBondScreenerTrancheCollectionResponse> {
    return this.abortableCall(new Request('cfr.bondscreener.corp.get_selected_tranches', payload));
  }

  async getSelectedAbsTranches(
    payload: IGetSelectedItemsRequest,
  ): Promise<IAbsBondScreenerTrancheCollectionResponse> {
    return this.abortableCall(new Request('cfr.bondscreener.abs.get_selected_tranches', payload));
  }

  async getSelectedAbsDeals(
    payload: IGetSelectedItemsRequest,
  ): Promise<IAbsBondScreenerDealCollectionResponse> {
    return this.abortableCall(new Request('cfr.bondscreener.abs.get_selected_deals', payload));
  }

  @withCache
  async getFilterBoundaries(
    filterType: 'corp' | 'abs',
  ): Promise<TBondScreenerFilterBoundaries> {
    const request = new Request('cfr.bondscreener.filter.get_boundaries', { filterType });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async getCompanyList(payload: ICompanyListPayload): Promise<IBondScreenerCompanyListResponse> {
    const request = new Request('cfr.bondscreener.filter.company.list', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async searchLeads(payload: ISearchLeadsPayload): Promise<ILeadCompany[]> {
    const request = new Request('cfr.company.corp.search_leads', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload.collection;
  }

  async searchAbsLeads(payload: IAbsSearchLeadsPayload): Promise<ILeadCompany[]> {
    const request = new Request('cfr.company.abs.search_leads', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload.collection;
  }
}

export default BondScreenersRepository;
